import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { MEETING_DATE_PRESET_OPTIONS, MEETING_DATE_PRESETS } from '../../../constants/meetingDatePresets';

/**
 * MeetingDatePresetSelector - A dropdown selector for filtering meetings by date range
 * Includes custom date pickers when "Custom" option is selected
 *
 * @param {string} datePreset - The selected preset value
 * @param {Function} onDatePresetChange - Callback when preset changes (value) => void
 * @param {Date|null} dateFrom - Custom date from value
 * @param {Function} onDateFromChange - Callback when dateFrom changes (date) => void
 * @param {Date|null} dateTo - Custom date to value
 * @param {Function} onDateToChange - Callback when dateTo changes (date) => void
 * @param {boolean} disabled - Whether the selector is disabled
 * @param {string} label - Label for the selector
 * @param {string} size - Size of the select ('small' | 'medium')
 * @param {Object} sx - Additional styles for the FormControl
 */
const MeetingDatePresetSelector = ({
  datePreset = '',
  onDatePresetChange,
  dateFrom = null,
  onDateFromChange,
  dateTo = null,
  onDateToChange,
  disabled = false,
  label = 'Date Range',
  size = 'small',
  sx = {},
}) => {
  const isCustomDateRange = datePreset === MEETING_DATE_PRESETS.CUSTOM;

  const selectSx = {
    backgroundColor: 'white',
    height: '40px',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E2E8F0',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#2563eb',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#2563eb',
    },
  };

  const datePickerSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'white',
      height: '40px',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#E2E8F0',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#2563eb',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#2563eb',
      },
    },
    '& .MuiInputBase-input': {
      padding: '8px 14px',
      fontSize: '14px',
    },
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', ...sx }}>
      {/* Date Preset */}
      <FormControl size={size} sx={{ minWidth: isCustomDateRange ? 120 : 150 }} disabled={disabled}>
        {label && <InputLabel>{label}</InputLabel>}
        <Select
          value={datePreset || ''}
          onChange={(e) => onDatePresetChange(e.target.value || null)}
          label={label}
          sx={selectSx}
        >
          {MEETING_DATE_PRESET_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Custom Date Pickers */}
      {isCustomDateRange && (
        <>
          <DatePicker
            label="From"
            value={dateFrom}
            onChange={(newValue) => onDateFromChange(newValue)}
            disabled={disabled}
            slotProps={{
              textField: {
                size: 'small',
                sx: { ...datePickerSx, width: 150 },
              },
            }}
            maxDate={dateTo || undefined}
          />
          <DatePicker
            label="To"
            value={dateTo}
            onChange={(newValue) => onDateToChange(newValue)}
            disabled={disabled}
            slotProps={{
              textField: {
                size: 'small',
                sx: { ...datePickerSx, width: 150 },
              },
            }}
            minDate={dateFrom || undefined}
          />
        </>
      )}
    </Box>
  );
};

export default MeetingDatePresetSelector;
